import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useLocation, useNavigationType } from "react-router-dom"
import { cn } from "@/shared/lib/utils"
import { AdminNavigation, NAV_DURATION, NAV_EASE } from "./admin-navigation"

const positions = new Map<string, number>()
const COLLAPSED_KEY = "kmk:nav-collapsed"

function readCollapsed(): boolean {
	try {
		return localStorage.getItem(COLLAPSED_KEY) === "1"
	} catch {
		return false
	}
}

/** Свёрнутое меню помнится в браузере, ⌘\ или Ctrl+\ переключают его. */
function useNavCollapsed() {
	const [collapsed, setCollapsed] = useState(readCollapsed)
	useEffect(() => {
		try {
			localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0")
		} catch {
			// Хранилище недоступно: меню просто не запомнит состояние.
		}
	}, [collapsed])
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key === "\\") {
				event.preventDefault()
				setCollapsed((value) => !value)
			}
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [])
	return [collapsed, () => setCollapsed((value) => !value)] as const
}

export function Layout({ children }: { children: ReactNode }) {
	const location = useLocation()
	const navigationType = useNavigationType()
	// Холст сценариев занимает весь экран: боковая колонка в нём мешает.
	const editor = location.pathname.startsWith("/admin/scenarios/")
	const previousPath = useRef("")
	const [collapsed, toggleCollapsed] = useNavCollapsed()
	useLayoutEffect(() => {
		const key = location.key
		if (navigationType === "POP") {
			const target = positions.get(key) ?? 0
			let tries = 0
			const restore = () => {
				window.scrollTo(0, target)
				return window.scrollY >= target - 1
			}
			restore()
			const timer = window.setInterval(() => {
				if (restore() || ++tries > 30) window.clearInterval(timer)
			}, 50)
			previousPath.current = location.pathname
			return () => {
				window.clearInterval(timer)
				positions.set(key, window.scrollY)
			}
		}
		if (previousPath.current !== location.pathname) window.scrollTo(0, 0)
		previousPath.current = location.pathname
		return () => {
			positions.set(key, window.scrollY)
		}
	}, [location.key, location.pathname, navigationType])
	return (
		<div
			style={{ "--nav-width": collapsed ? "4.5rem" : "15rem" } as CSSProperties}
			className={cn(
				`min-h-dvh bg-background transition-[padding] ${NAV_DURATION} ${NAV_EASE}`,
				!editor && "lg:pl-[var(--nav-width)]",
			)}
		>
			{!editor && <AdminNavigation collapsed={collapsed} onToggle={toggleCollapsed} />}
			<main>{children}</main>
		</div>
	)
}
